/**
 * useSubscriptions.ts — fetch subscription queue data
 *
 * Calls GET /api/broker/v1/subscriptions?approval_state=requested (operator)
 * or GET /api/broker/v1/subscriptions (non-operator: own subscriptions).
 *
 * WP-SL-V4 / DOPLA-687
 */

import { useEffect, useReducer, useCallback } from 'react';

export interface SubscriptionRow {
  id: string;
  service_id: string;
  plan_id: string;
  tenant_id: string | null;
  user_subject: string;
  status: string;
  approval_state: string;
  trial_ends_at: string | null;
  auto_created: boolean;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  terms_snapshot: Record<string, unknown> | null;
  bundle_id: string | null;
  created_at: string;
  service_name: string | null;
  plan_name: string | null;
  tenant_name: string | null;
}

export type SubscriptionsState =
  | { tag: 'idle' }
  | { tag: 'loading' }
  | { tag: 'loaded'; subscriptions: SubscriptionRow[] }
  | { tag: 'error'; errorCode?: string };

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; subscriptions: SubscriptionRow[] }
  | { type: 'FETCH_ERROR'; errorCode?: string };

function reducer(_: SubscriptionsState, action: Action): SubscriptionsState {
  switch (action.type) {
    case 'FETCH_START':   return { tag: 'loading' };
    case 'FETCH_SUCCESS': return { tag: 'loaded', subscriptions: action.subscriptions };
    case 'FETCH_ERROR':   return { tag: 'error', errorCode: action.errorCode };
    default:              return _;
  }
}

export function useSubscriptions(
  token: string | null,
  filter?: { approval_state?: string; tenant_id?: string },
) {
  const [state, dispatch] = useReducer(reducer, { tag: 'idle' });

  const load = useCallback(() => {
    if (!token) return;
    dispatch({ type: 'FETCH_START' });
    const params = new URLSearchParams();
    if (filter?.approval_state) params.set('approval_state', filter.approval_state);
    if (filter?.tenant_id)      params.set('tenant_id', filter.tenant_id);
    const qs = params.toString() ? `?${params}` : '';
    fetch(`/api/broker/v1/subscriptions${qs}`, {
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) { dispatch({ type: 'FETCH_ERROR', errorCode: String(res.status) }); return; }
        return res.json() as Promise<{ subscriptions: SubscriptionRow[] }>;
      })
      .then((data) => {
        if (data) dispatch({ type: 'FETCH_SUCCESS', subscriptions: data.subscriptions });
      })
      .catch(() => dispatch({ type: 'FETCH_ERROR', errorCode: 'NETWORK_ERROR' }));
  }, [token, filter?.approval_state, filter?.tenant_id]);

  useEffect(() => { load(); }, [load]);

  return { state, reload: load };
}
